import { useState, useCallback } from 'react';
import { CLIENT_SEARCH_CRITERIA } from '../constants/clientSearchCriteria';

const STORAGE_KEY = 'sequela.tasks.clientSearchCriteria';

const VALID_VALUES = CLIENT_SEARCH_CRITERIA.map(c => c.value);

const buildDefaults = () => ({
  visibleCriteria: VALID_VALUES.slice(),
  order: VALID_VALUES.slice()
});

const isValidPreferences = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (!Array.isArray(data.visibleCriteria) || !Array.isArray(data.order)) return false;
  if (data.visibleCriteria.length === 0) return false;
  if (!data.visibleCriteria.every(v => VALID_VALUES.includes(v))) return false;
  if (!data.order.every(v => VALID_VALUES.includes(v))) return false;
  return true;
};

const readPreferences = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaults();
    const parsed = JSON.parse(raw);
    return isValidPreferences(parsed) ? parsed : buildDefaults();
  } catch {
    return buildDefaults();
  }
};

const writePreferences = (prefs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage no disponible (modo privado, cuota excedida); la preferencia
    // simplemente no persiste, no debe romper la pantalla.
  }
};

export const useTaskClientCriteriaPreferences = () => {
  const [preferences, setPreferences] = useState(readPreferences);

  const orderedVisibleCriteria = preferences.order
    .filter(value => preferences.visibleCriteria.includes(value))
    .map(value => CLIENT_SEARCH_CRITERIA.find(c => c.value === value))
    .filter(Boolean);

  const setVisibleCriteria = useCallback((visibleCriteria) => {
    if (!Array.isArray(visibleCriteria) || visibleCriteria.length === 0) return;
    setPreferences(prev => {
      const next = { ...prev, visibleCriteria };
      writePreferences(next);
      return next;
    });
  }, []);

  const setOrder = useCallback((order) => {
    if (!Array.isArray(order)) return;
    setPreferences(prev => {
      const next = { ...prev, order };
      writePreferences(next);
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = buildDefaults();
    setPreferences(defaults);
    writePreferences(defaults);
  }, []);

  return {
    visibleCriteria: preferences.visibleCriteria,
    order: preferences.order,
    orderedVisibleCriteria,
    setVisibleCriteria,
    setOrder,
    resetToDefaults
  };
};
